/**
 * Global notification utilities for connecting API service with centralized notifications
 * This provides a bridge between the API service error handling and the notification system
 */

import type { ApiErrorResponse } from '@/types/services'

// Define types for the notification system
interface NotificationOptions {
  duration?: number
  closable?: boolean
  title?: string
  action?: {
    label: string
    callback: () => void
  }
  onRetry?: () => void
}

interface NotificationInstance {
  notifySuccess: (message: string, options?: NotificationOptions) => string
  notifyError: (message: string, options?: NotificationOptions) => string
  notifyWarning: (message: string, options?: NotificationOptions) => string
  notifyInfo: (message: string, options?: NotificationOptions) => string
  notifyConfirm: (message: string, options?: NotificationOptions) => string
  notifyWithAction: (message: string, options?: NotificationOptions) => string
  clearAllNotifications: () => void
}

// Extend window interface for global notifications
declare global {
  interface Window {
    notifySuccess?: (message: string, options?: NotificationOptions) => string
    notifyError?: (message: string, options?: NotificationOptions) => string
    notifyWarning?: (message: string, options?: NotificationOptions) => string
    notifyInfo?: (message: string, options?: NotificationOptions) => string
    notifyConfirm?: (message: string, options?: NotificationOptions) => string
    notifyWithAction?: (message: string, options?: NotificationOptions) => string
    clearAllNotifications?: () => void
  }
}

/**
 * Global notifications instance
 * Created once and reused throughout the application
 */
let globalNotifications: NotificationInstance | null = null

/**
 * Initialize global notifications
 * This should be called during app bootstrap
 */
export function initializeGlobalNotifications(): NotificationInstance {
  if (!globalNotifications) {
    // Prefer using the notification functions injected on window by the Vue plugin
    // so that calls from non-Vue modules (services/utils) will be rendered by the
    // NotificationContainer via the useNotifications composable.
    const win: any = typeof window !== 'undefined' ? window : {}

    const hasWindowNotifiers = !!(
      win.notifySuccess || win.notifyError || win.notifyWarning || win.notifyInfo
    )

    if (hasWindowNotifiers) {
      globalNotifications = {
        notifySuccess: (message: string, options?: NotificationOptions) => {
          try { return win.notifySuccess(message, options) } catch (e) { console.warn(e); return 'success-id' }
        },
        notifyError: (message: string, options?: NotificationOptions) => {
          try { return win.notifyError(message, options) } catch (e) { console.warn(e); return 'error-id' }
        },
        notifyWarning: (message: string, options?: NotificationOptions) => {
          try { return win.notifyWarning(message, options) } catch (e) { console.warn(e); return 'warning-id' }
        },
        notifyInfo: (message: string, options?: NotificationOptions) => {
          try { return win.notifyInfo(message, options) } catch (e) { console.warn(e); return 'info-id' }
        },
        notifyConfirm: (message: string, options?: NotificationOptions) => {
          try { return win.notifyConfirm(message, options) } catch (e) { console.warn(e); return 'confirm-id' }
        },
        notifyWithAction: (message: string, options?: NotificationOptions) => {
          try { return win.notifyWithAction(message, options) } catch (e) { console.warn(e); return 'action-id' }
        },
        clearAllNotifications: () => {
          try { return win.clearAllNotifications() } catch (e) { console.warn(e); return undefined }
        }
      }
    } else {
      // If plugin wasn't installed yet (e.g. called early), provide console fallback
      globalNotifications = {
        notifySuccess: (message: string, options?: NotificationOptions) => {
          console.log('SUCCESS:', message, options)
          return 'success-id'
        },
        notifyError: (message: string, options?: NotificationOptions) => {
          console.error('ERROR:', message, options)
          return 'error-id'
        },
        notifyWarning: (message: string, options?: NotificationOptions) => {
          console.warn('WARNING:', message, options)
          return 'warning-id'
        },
        notifyInfo: (message: string, options?: NotificationOptions) => {
          console.info('INFO:', message, options)
          return 'info-id'
        },
        notifyConfirm: (message: string, options?: NotificationOptions) => {
          console.log('CONFIRM:', message, options)
          return 'confirm-id'
        },
        notifyWithAction: (message: string, options?: NotificationOptions) => {
          console.log('ACTION:', message, options)
          return 'action-id'
        },
        clearAllNotifications: () => {
          console.log('CLEAR ALL NOTIFICATIONS')
        }
      }
    }
  }

  return globalNotifications
}

/**
 * Get the global notifications instance
 * @returns Global notifications instance
 */
export function getGlobalNotifications(): NotificationInstance {
  if (!globalNotifications) {
    globalNotifications = initializeGlobalNotifications()
  }
  return globalNotifications
}

/**
 * Show API error notification with proper categorization
 * @param errorInfo - Structured error information from API service
 * @param options - Additional notification options
 */
export function showApiErrorNotification(
  errorInfo: ApiErrorResponse, 
  options: NotificationOptions = {}
): string {
  const notifications = getGlobalNotifications()
  
  const defaultOptions: NotificationOptions = {
    duration: 7000, // Default duration
    closable: true,
    title: options.title || getErrorTitle(errorInfo.code?.toString() || 'UNKNOWN')
  }
  
  const mergedOptions = { ...defaultOptions, ...options }
  
  // Build detailed error message
  let detailedMessage = errorInfo.message
  
  // Add error details if available
  if (errorInfo.details) {
    const details: string[] = []
    
    // Add field validation errors if present
    if (errorInfo.details.fieldErrors && typeof errorInfo.details.fieldErrors === 'object') {
      const fieldErrors = errorInfo.details.fieldErrors as Record<string, string>
      Object.entries(fieldErrors).forEach(([field, message]) => {
        details.push(`${field}: ${message}`)
      })
    }
    
    // Add validation errors if present and different from fieldErrors
    if (errorInfo.details.validationErrors && 
        typeof errorInfo.details.validationErrors === 'object' &&
        !errorInfo.details.fieldErrors) {
      const validationErrors = errorInfo.details.validationErrors as Record<string, string>
      Object.entries(validationErrors).forEach(([field, message]) => {
        details.push(`${field}: ${message}`)
      })
    }
    
    // Append details to message if any were found
    if (details.length > 0) {
      detailedMessage += '\n' + details.join('\n')
    }
  }
  
  
  // Choose notification type based on error code/type
  const errorType = errorInfo.code?.toString() || 'UNKNOWN'
  
  switch (errorType) {
    case 'VALIDATION_ERROR':
      return notifications.notifyWarning(detailedMessage, {
        ...mergedOptions,
        title: mergedOptions.title || 'Validation Error',
        duration: 8000 // Validation errors should be visible longer
      })
      
    case 'UNAUTHORIZED':
    case 'FORBIDDEN':
      return notifications.notifyError(detailedMessage, {
        ...mergedOptions,
        title: mergedOptions.title || 'Access Denied',
        duration: 0 // Keep authorization errors open
      })
      
    case 'NOT_FOUND':
      return notifications.notifyWarning(detailedMessage, {
        ...mergedOptions,
        title: mergedOptions.title || 'Not Found'
      })
      
    case 'CONFLICT':
      return notifications.notifyWarning(detailedMessage, {
        ...mergedOptions,
        title: mergedOptions.title || 'Conflict'
      })
      
    case 'TOO_MANY_REQUESTS':
      return notifications.notifyWarning(detailedMessage, {
        ...mergedOptions,
        title: mergedOptions.title || 'Rate Limited',
        duration: 10000 // Show longer for rate limiting
      })
      
    case 'NETWORK_ERROR':
      return notifications.notifyError(detailedMessage, {
        ...mergedOptions,
        title: mergedOptions.title || 'Network Error',
        action: {
          label: 'Retry',
          callback: options.onRetry || (() => window.location.reload())
        }
      })
      
    case 'FILE_TOO_LARGE':
      return notifications.notifyError(detailedMessage, {
        ...mergedOptions,
        title: mergedOptions.title || 'File Too Large',
        duration: 8000 // Show for a reasonable time
      })
      
    case 'SERVICE_UNAVAILABLE':
    case 'INTERNAL_SERVER_ERROR':
      return notifications.notifyError(detailedMessage, {
        ...mergedOptions,
        title: mergedOptions.title || 'Server Error',
        action: {
          label: 'Retry',
          callback: options.onRetry || (() => window.location.reload())
        }
      })
      
    default:
      return notifications.notifyError(detailedMessage, {
        ...mergedOptions,
        title: mergedOptions.title || 'Error'
      })
  }
}

/**
 * Show success notification
 * @param message - Success message
 * @param options - Notification options
 */
export function showApiSuccessNotification(
  message: string, 
  options: NotificationOptions = {}
): string {
  const notifications = getGlobalNotifications()
  return notifications.notifySuccess(message, {
    duration: 4000,
    ...options
  })
}

/**
 * Show info notification
 * @param message - Info message
 * @param options - Notification options
 */
export function showApiInfoNotification(
  message: string, 
  options: NotificationOptions = {}
): string {
  const notifications = getGlobalNotifications()
  return notifications.notifyInfo(message, {
    duration: 5000,
    ...options
  })
}

/**
 * Show warning notification
 * @param message - Warning message
 * @param options - Notification options
 */
export function showApiWarningNotification(
  message: string, 
  options: NotificationOptions = {}
): string {
  const notifications = getGlobalNotifications()
  return notifications.notifyWarning(message, {
    duration: 7000,
    ...options
  })
}

/**
 * Get appropriate error title based on error type
 * @param errorType - The error type
 * @returns Human-readable error title
 */
function getErrorTitle(errorType: string): string {
  switch (errorType) {
    case 'VALIDATION_ERROR':
      return 'Validation Error'
    case 'UNAUTHORIZED':
      return 'Authentication Required'
    case 'FORBIDDEN':
      return 'Access Denied'
    case 'NOT_FOUND':
      return 'Not Found'
    case 'CONFLICT':
      return 'Conflict'
    case 'TOO_MANY_REQUESTS':
      return 'Rate Limited'
    case 'NETWORK_ERROR':
      return 'Network Error'
    case 'FILE_TOO_LARGE':
      return 'File Too Large'
    case 'SERVICE_UNAVAILABLE':
      return 'Service Unavailable'
    case 'INTERNAL_SERVER_ERROR':
      return 'Server Error'
    default:
      return 'Error'
  }
}

export default {
  initializeGlobalNotifications,
  getGlobalNotifications,
  showApiErrorNotification,
  showApiSuccessNotification,
  showApiInfoNotification,
  showApiWarningNotification
}
